/**
 * useTotpEntries.ts
 *
 * Custom hooks for TOTP data fetching and mutations.
 * Provides typed React Query hooks for all TOTP operations.
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '@/base/utils/api';
import { useToastStore } from '@/base/stores/toastStore';

// ============================================================================
// Axios Error Helper
// ============================================================================

interface AxiosErrorShape {
  response?: { data?: { message?: string } };
  message?: string;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const e = error as AxiosErrorShape;
  return e?.response?.data?.message || e?.message || fallback;
}

// ============================================================================
// Types
// ============================================================================

export interface TotpEntry {
  id: string;
  issuer: string;
  accountLabel: string;
  algorithm: 'SHA1' | 'SHA256' | 'SHA512';
  digits: number;
  period: number;
  displayOrder: number;
  isFavorite: boolean;
  isDisabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastUsedAt: string | null;
}

export interface TotpCode {
  code: string;
  remainingSeconds: number;
  expiresAt: string;
}

export interface TotpBatchCodesRequest {
  entryIds: string[];
}

export interface TotpBatchCodesResponse {
  codes: Record<string, TotpCode>;
}

export interface CreateTotpEntryRequest {
  issuer: string;
  accountLabel: string;
  secret: string;
  algorithm?: 'SHA1' | 'SHA256' | 'SHA512';
  digits?: number;
  period?: number;
  isFavorite?: boolean;
}

export interface UpdateTotpEntryRequest {
  issuer?: string;
  accountLabel?: string;
  algorithm?: 'SHA1' | 'SHA256' | 'SHA512';
  digits?: number;
  period?: number;
  isFavorite?: boolean;
  isDisabled?: boolean;
  displayOrder?: number;
}

export interface ReorderTotpEntriesRequest {
  entryIds: string[];
}

export interface ParseTotpUriRequest {
  uri: string;
}

export interface ParsedTotpData {
  issuer: string;
  accountLabel: string;
  secret: string;
  algorithm: 'SHA1' | 'SHA256' | 'SHA512';
  digits: number;
  period: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const totpKeys = {
  all: ['totp'] as const,
  entries: () => [...totpKeys.all, 'entries'] as const,
  entry: (id: string) => [...totpKeys.all, 'entry', id] as const,
  code: (id: string) => [...totpKeys.all, 'code', id] as const,
  batchCodes: (ids: string[]) => [...totpKeys.all, 'batch-codes', ids.sort().join(',')] as const,
};

// ============================================================================
// Fetch Hooks
// ============================================================================

/**
 * Fetch all TOTP entries for the current user
 */
export function useTotpEntries(): UseQueryResult<TotpEntry[], Error> {
  return useQuery({
    queryKey: totpKeys.entries(),
    queryFn: async () => {
      const response = await apiClient.get<{ entries: TotpEntry[] }>('/v1/cvs/totp/entries');
      return response.data.entries;
    },
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a single TOTP code for an entry (only when vault is unlocked)
 */
export function useTotpCode(entryId: string | null, enabled: boolean = true): UseQueryResult<TotpCode, Error> {
  return useQuery({
    queryKey: totpKeys.code(entryId || ''),
    queryFn: async () => {
      if (!entryId) throw new Error('Entry ID is required');
      const response = await apiClient.get<TotpCode>(`/v1/cvs/totp/entries/${entryId}/code`);
      return response.data;
    },
    enabled: enabled && !!entryId,
    refetchInterval: (query) => {
      // Refetch when code is about to expire (5 seconds before)
      const data = query.state.data;
      if (!data) return false;
      const refetchIn = Math.max(0, data.remainingSeconds - 5) * 1000;
      return refetchIn > 0 ? refetchIn : 1000;
    },
    staleTime: 0, // Always fresh to trigger refetch interval
  });
}

/**
 * Fetch multiple TOTP codes in a single batch request
 */
export function useTotpBatchCodes(entryIds: string[], enabled: boolean = true): UseQueryResult<TotpBatchCodesResponse, Error> {
  return useQuery({
    queryKey: totpKeys.batchCodes(entryIds),
    queryFn: async () => {
      const response = await apiClient.post<TotpBatchCodesResponse>('/v1/cvs/totp/entries/batch-codes', {
        entryIds,
      });
      return response.data;
    },
    enabled: enabled && entryIds.length > 0,
    refetchInterval: (query) => {
      // Refetch based on the earliest expiring code
      const data = query.state.data;
      if (!data) return false;
      const minRemaining = Math.min(
        ...Object.values(data.codes).map((code: TotpCode) => code.remainingSeconds)
      );
      const refetchIn = Math.max(0, minRemaining - 5) * 1000;
      return refetchIn > 0 ? refetchIn : 1000;
    },
    staleTime: 0,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new TOTP entry
 */
export function useCreateTotpEntry(): UseMutationResult<TotpEntry, unknown, CreateTotpEntryRequest> {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: async (data: CreateTotpEntryRequest) => {
      const response = await apiClient.post<{ entry: TotpEntry }>('/v1/cvs/totp/entries', data);
      return response.data.entry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: totpKeys.entries() });
      toast.success('TOTP entry created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to create TOTP entry'));
    },
  });
}

/**
 * Update an existing TOTP entry
 */
export function useUpdateTotpEntry(): UseMutationResult<TotpEntry, unknown, { id: string; data: UpdateTotpEntryRequest }> {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTotpEntryRequest }) => {
      const response = await apiClient.patch<{ entry: TotpEntry }>(`/v1/cvs/totp/entries/${id}`, data);
      return response.data.entry;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: totpKeys.entries() });
      queryClient.invalidateQueries({ queryKey: totpKeys.entry(data.id) });
      toast.success('TOTP entry updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update TOTP entry'));
    },
  });
}

/**
 * Delete a TOTP entry
 */
export function useDeleteTotpEntry(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/v1/cvs/totp/entries/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: totpKeys.entries() });
      toast.success('TOTP entry deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete TOTP entry'));
    },
  });
}

/**
 * Reorder TOTP entries
 */
export function useReorderTotpEntries(): UseMutationResult<void, unknown, ReorderTotpEntriesRequest> {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: async (data: ReorderTotpEntriesRequest) => {
      await apiClient.post('/v1/cvs/totp/entries/reorder', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: totpKeys.entries() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to reorder entries'));
    },
  });
}

/**
 * Parse a TOTP URI (otpauth://)
 */
export function useParseTotpUri(): UseMutationResult<ParsedTotpData, unknown, ParseTotpUriRequest> {
  const toast = useToastStore();

  return useMutation({
    mutationFn: async (data: ParseTotpUriRequest) => {
      const response = await apiClient.post<ParsedTotpData>('/v1/cvs/totp/parse-uri', data);
      return response.data;
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to parse TOTP URI'));
    },
  });
}

// ============================================================================
// Import Hooks
// ============================================================================

export interface ImportTotpEntryInput {
  otpauthUri?: string;
  issuer?: string;
  accountLabel?: string;
  secret?: string;
  algorithm?: 'SHA1' | 'SHA256' | 'SHA512';
  digits?: number;
  period?: number;
}

export interface BatchImportTotpRequest {
  entries: ImportTotpEntryInput[];
}

export interface ImportError {
  index: number;
  error: string;
}

export interface BatchImportTotpResponse {
  imported: TotpEntry[];
  errors: ImportError[];
}

export interface ImportGoogleAuthRequest {
  migrationUri: string;
}

/**
 * Batch import TOTP entries
 */
export function useBatchImportTotp(): UseMutationResult<BatchImportTotpResponse, unknown, BatchImportTotpRequest> {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: async (data: BatchImportTotpRequest) => {
      const response = await apiClient.post<BatchImportTotpResponse>('/v1/cvs/totp/entries/import', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: totpKeys.entries() });
      const { imported, errors } = data;
      if (imported.length > 0) {
        toast.success(`Successfully imported ${imported.length} ${imported.length === 1 ? 'entry' : 'entries'}`);
      }
      if (errors.length > 0) {
        toast.error(`Failed to import ${errors.length} ${errors.length === 1 ? 'entry' : 'entries'}`);
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to import TOTP entries'));
    },
  });
}

/**
 * Import from Google Authenticator migration URI
 */
export function useImportGoogleAuth(): UseMutationResult<BatchImportTotpResponse, unknown, ImportGoogleAuthRequest> {
  const queryClient = useQueryClient();
  const toast = useToastStore();

  return useMutation({
    mutationFn: async (data: ImportGoogleAuthRequest) => {
      const response = await apiClient.post<BatchImportTotpResponse>('/v1/cvs/totp/import/google-auth', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: totpKeys.entries() });
      const { imported, errors } = data;
      if (imported.length > 0) {
        toast.success(`Successfully imported ${imported.length} ${imported.length === 1 ? 'entry' : 'entries'} from Google Authenticator`);
      }
      if (errors.length > 0) {
        toast.error(`Failed to import ${errors.length} ${errors.length === 1 ? 'entry' : 'entries'}`);
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to import from Google Authenticator'));
    },
  });
}
